<script setup lang="ts">
import {
  HgPaginationSelector,
  SupportedPlatforms,
  type HgSupportedLanguage,
} from '@smg-real-estate/domus';
import type { Locale } from '@smg-real-estate/geo-libs';
import { FeaturedPlacement } from '@smg-real-estate/seeker-app-shared-components';
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { DEFAULT_AGENCY_PAGE_LIST_SIZE } from '@/api/customerMetaApi';
import AgencyCard from '@/components/AgencyCard/AgencyCard.vue';
import AgencyCardPlaceHolder from '@/components/AgencyCard/AgencyCardPlaceHolder.vue';
import ResultsHeaderPlaceholder from '@/components/ResultsHeaderPlaceholder/ResultsHeaderPlaceholder.vue';
import SortingSelect from '@/components/SortingSelect/SortingSelect.vue';
import ZeroSearchResult from '@/components/ZeroSearchResult/ZeroSearchResult.vue';
import { isPlatformHg, PlatformEnvs } from '@/helpers/Platform/platform';
import { generateAgencyProfilePagePath } from '@/helpers/Url/Url';
import { useLocale } from '@/router/routeMeta';
import { useAgencyProfilesStore } from '@/stores/agencyProfiles';
import { trackPaginationClick, trackSearchResultsAgencyCardClick } from '@/tracking/tracking';
import { type Agency } from '@/types';
import { captureAction } from '@smg-real-estate/browser-rum';
import { useHead } from '@unhead/vue';
import { useRoute } from 'vue-router';

const agencyProfilesStore = useAgencyProfilesStore();
const { searchedGeoIds } = storeToRefs(agencyProfilesStore);

const platform = isPlatformHg() ? SupportedPlatforms.hg : SupportedPlatforms.is24;
const isProduction = import.meta.env.VUE_APP_ENVIRONMENT === PlatformEnvs.prod;
const containerRef = ref<HTMLElement | null>(null);

const locale = useLocale();

const { t } = useI18n();
const route = useRoute();

const props = defineProps<{
  agencies: Agency[];
  agencyListCurrentPage: number;
  pageCount: number;
  language: HgSupportedLanguage;
  loadingAgencies: boolean;
  totalAgenciesCount: number;
  locationsForSeo: string;
}>();

const pageRoute = (page: number) => {
  return agencyProfilesStore.nextPageRoute(page, locale.value as unknown as Locale);
};

const translations = computed(() => ({
  featuredAgenciesSectionTitle: t('agencyList.featuredAgencies').toString(),
  featuredAgenciesPreviewBadge: t('agencies.featuredAgenciesPreviewBadge').toString(),
  featuredAgenciesDescriptionsTitle: t('agencies.featuredAgenciesDescriptionsTitle').toString(),
  featuredAgenciesServicesTitle: t('agencies.featuredAgenciesServicesTitle').toString(),
  featuredAgenciesGenericDescription: t('agencies.featuredAgenciesGenericDescription').toString(),
  agencyCTA: t('agencies.agencyCTA').toString(),
  searchResults: t('agencyList.searchResults', {
    numberOfResult: props.totalAgenciesCount,
    location: props.locationsForSeo,
  }).toString(),
}));

const agenciesPlaceHolderCount = computed(() => (props.loadingAgencies ? 20 : 0));

const getAgencyDescription = (agency: Agency): string | undefined =>
  agency.descriptions && agency.descriptions[locale.value as unknown as string];

const handleSearchResultsAgencyCardClick = (
  clickedSearchResultsAgencyCard: Agency,
  index: number,
) => {
  captureAction('Agency Card Click', {
    customerId: clickedSearchResultsAgencyCard.customerId,
    position:
      (agencyProfilesStore.agencyListCurrentPage - 1) * DEFAULT_AGENCY_PAGE_LIST_SIZE + index + 1,
  });
  trackSearchResultsAgencyCardClick({
    clickedSearchResultsAgencyCard,
    position:
      (agencyProfilesStore.agencyListCurrentPage - 1) * DEFAULT_AGENCY_PAGE_LIST_SIZE + index + 1,
  });
};

/**
 * Build ItemList JSON-LD describing the agencies shown on the current page.
 * Provides crawlers with a semantic list of agency Organizations so each
 * result can pass link equity to its profile page.
 */
const generateItemListStructuredData = () => {
  if (props.loadingAgencies || !props.agencies.length) {
    return [];
  }

  const startPosition = (props.agencyListCurrentPage - 1) * DEFAULT_AGENCY_PAGE_LIST_SIZE;

  const itemListElement = props.agencies.map((agency, index) => {
    const url = generateAgencyProfilePagePath(
      props.language,
      agency.profilePageUrlKeyword,
      agency.customerId,
    );

    const organization: Record<string, unknown> = {
      '@type': 'Organization',
      name: agency.customerName,
      url,
    };

    if (agency.logo) {
      organization.image = agency.logo;
    }

    return {
      '@type': 'ListItem',
      position: startPosition + index + 1,
      item: organization,
    };
  });

  return [
    {
      type: 'application/ld+json' as const,
      textContent: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        itemListElement,
      }),
    },
  ];
};

useHead({
  script: computed(() => generateItemListStructuredData()),
});

onMounted(() => {
  const demoCustomerId = new URLSearchParams(window.location.search).get('demoCustomerId');
  if (demoCustomerId) {
    // Disable the scrollRestoration, which interferes with the scrollIntoView
    history.scrollRestoration && (history.scrollRestoration = 'manual');
    setTimeout(() => {
      containerRef.value?.scrollIntoView({ behavior: 'smooth' });
    }, 300);
  }
});
</script>

<template>
  <div ref="containerRef" :class="$style.container">
    <div :class="$style.featuredPlacementWrapper">
      <FeaturedPlacement
        v-if="searchedGeoIds"
        data-dd-action-name="Featured Agencies"
        :currentPage="agencyListCurrentPage"
        :language="language"
        :geoIds="searchedGeoIds"
        :translations="translations"
        :isProduction="isProduction"
        :platform="platform"
      >
        <template #agencyText>
          {{ $t('agencies.featuredAgenciesGenericDescription') }}
        </template>
      </FeaturedPlacement>
    </div>

    <ResultsHeaderPlaceholder v-if="loadingAgencies" />
    <div v-else :class="$style.resultsHeader">
      <h2 :class="{ [$style.invisible]: loadingAgencies, [$style.resultsHeaderText]: true }">
        {{
          t('agencyList.searchResults', {
            numberOfResult: totalAgenciesCount,
            location: locationsForSeo,
          })
        }}
      </h2>
      <SortingSelect />
    </div>
    <div v-if="!loadingAgencies && !agencies.length"><ZeroSearchResult /></div>
    <div :class="$style.agencyList">
      <AgencyCard
        v-for="(agency, index) in agencies"
        :key="agency.customerId"
        :agencyLogo="agency.logo"
        :agencyName="agency.customerName"
        :postalCode="agency.postalCode"
        :city="agency.city"
        :customerId="agency.customerId"
        :profilePageUrlKeyWord="agency.profilePageUrlKeyword"
        :language="language"
        :agents="agency.agents ?? []"
        :description="getAgencyDescription(agency)"
        :propertiesForRentCount="agency.propertiesForRentCount"
        :propertiesForBuyCount="agency.propertiesForBuyCount"
        @agencyCardClick="handleSearchResultsAgencyCardClick(agency, index)"
      />
      <AgencyCardPlaceHolder v-for="n in agenciesPlaceHolderCount" :key="`placeholder-${n}`" />
    </div>
    <div v-if="agencies.length > 0" :class="$style.agencyListPagination">
      <HgPaginationSelector
        :totalPages="pageCount"
        :currentPage="agencyListCurrentPage"
        :pageRoute="pageRoute"
        @paginationClicked="trackPaginationClick"
      />
    </div>
  </div>
</template>

<style module lang="scss">
@use '@/assets/variables' as *;

.container {
  margin-top: $space-200;
  margin-bottom: $space-200;
}

.featuredPlacementWrapper {
  margin-bottom: $space-150;
}

.agencyList {
  --columns-per-row: 1;

  display: grid;
  grid-template-columns: repeat(var(--columns-per-row), 1fr);
  gap: $space-150;
}

.resultsHeader {
  display: flex;
  flex-wrap: wrap;
  gap: $space-50;
  justify-content: space-between;
  margin-bottom: $space-150;

  > :last-child {
    padding-top: unset;
  }
}

.resultsHeaderText {
  margin: 0;

  @include heading-2;
}

.agencyListHeaderPlaceholder {
  height: $space-300 * 1.04;
}

.agencyListPagination {
  padding-top: $space-250;
}

.invisible {
  visibility: hidden;
}
</style>

<style lang="scss">
div[data-splide-el='controls'] {
  display: none !important;
}
</style>
